<template>
  <a-drawer width="35%" :visible="open" @close="onClose">
    <a-divider orientation="left">
      <b>{{ formTitle }}</b>
    </a-divider>
    <a-form-model ref="form" :model="form" :rules="rules" :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }">
      <a-form-model-item label="活动名称" prop="name">
        <a-input v-model="form.name" placeholder="请输入活动名称" allow-clear />
      </a-form-model-item>
      <a-form-model-item label="选择优惠券" prop="couponId">
        <a-select
          v-model="form.couponId"
          placeholder="请选择优惠券"
          show-search
          option-filter-prop="children"
          :loading="couponLoading"
          allow-clear
        >
          <a-select-option v-for="item in couponOptions" :key="item.id" :value="item.id">
            {{ getCouponLabel(item) }}
          </a-select-option>
        </a-select>
      </a-form-model-item>
      <a-form-model-item label="活动类型" prop="activityType">
        <a-select v-model="form.activityType" placeholder="请选择活动类型" allow-clear>
          <a-select-option v-for="item in activityTypeOptions" :key="item.value" :value="item.value">
            {{ item.label }}
          </a-select-option>
        </a-select>
      </a-form-model-item>
      <a-form-model-item label="开始时间" prop="startTime">
        <a-date-picker
          v-model="form.startTime"
          style="width: 100%"
          format="YYYY-MM-DD HH:mm:ss"
          valueFormat="YYYY-MM-DD HH:mm:ss"
          :show-time="true"
          allow-clear
        />
      </a-form-model-item>
      <a-form-model-item label="结束时间" prop="endTime">
        <a-date-picker
          v-model="form.endTime"
          style="width: 100%"
          format="YYYY-MM-DD HH:mm:ss"
          valueFormat="YYYY-MM-DD HH:mm:ss"
          :show-time="true"
          allow-clear
        />
      </a-form-model-item>
      <a-form-model-item label="状态" prop="status">
        <a-switch
          :checked="form.status === 1"
          checked-children="启用"
          un-checked-children="停用"
          @change="handleStatusChange"
        />
      </a-form-model-item>
      <a-form-model-item label="备注" prop="remark">
        <a-textarea v-model="form.remark" placeholder="请输入备注" :auto-size="{ minRows: 3, maxRows: 5 }" allow-clear />
      </a-form-model-item>
      <div class="bottom-control">
        <a-space>
          <a-button type="primary" :loading="submitLoading" @click="submitForm">
            保存
          </a-button>
          <a-button type="dashed" @click="cancel">
            取消
          </a-button>
        </a-space>
      </div>
    </a-form-model>
  </a-drawer>
</template>

<script>
import { listCoupon } from '@/api/coupon/coupon'
import { getCouponActivity, addCouponActivity, updateCouponActivity } from '@/api/coupon/couponActivity'

const ACTIVITY_TYPE_OPTIONS = [
  { label: '新用户注册', value: 'NEW_USER_REGISTER' }
]

function getDefaultForm () {
  return {
    id: null,
    name: null,
    couponId: undefined,
    couponName: null,
    couponPrice: null,
    activityType: 'NEW_USER_REGISTER',
    startTime: null,
    endTime: null,
    status: 1,
    createTime: null,
    remark: null
  }
}

export default {
  name: 'CouponActivityCreateForm',
  data () {
    return {
      open: false,
      couponLoading: false,
      submitLoading: false,
      formTitle: '',
      couponOptions: [],
      activityTypeOptions: ACTIVITY_TYPE_OPTIONS,
      form: getDefaultForm(),
      rules: {
        name: [
          { required: true, message: '活动名称不能为空', trigger: 'blur' }
        ],
        couponId: [
          { required: true, message: '请选择优惠券', trigger: 'change' }
        ],
        activityType: [
          { required: true, message: '请选择活动类型', trigger: 'change' }
        ],
        startTime: [
          { validator: this.validateStartTime, trigger: 'change' }
        ],
        endTime: [
          { validator: this.validateEndTime, trigger: 'change' }
        ]
      }
    }
  },
  methods: {
    onClose () {
      this.cancel()
    },
    reset () {
      this.form = getDefaultForm()
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetFields()
        }
      })
    },
    handleAdd () {
      this.reset()
      this.formTitle = '添加活动'
      this.open = true
      this.getCouponOptions()
    },
    handleUpdate (row, ids) {
      const id = row ? row.id : (Array.isArray(ids) ? ids[0] : ids)
      this.reset()
      this.formTitle = '修改活动'
      Promise.all([this.getCouponOptions(), getCouponActivity({ id })]).then(([, response]) => {
        const data = (response && response.data) || {}
        this.form = {
          ...getDefaultForm(),
          ...data,
          couponId: data.couponId === null || data.couponId === undefined ? undefined : data.couponId,
          status: Number(data.status) === 0 ? 0 : 1
        }
        this.open = true
      })
    },
    cancel () {
      this.open = false
      this.reset()
    },
    getCouponOptions () {
      this.couponLoading = true
      return listCoupon().then(response => {
        const data = response && response.data
        this.couponOptions = Array.isArray(data) ? data : ((data && data.records) || [])
      }).finally(() => {
        this.couponLoading = false
      })
    },
    getCouponLabel (item) {
      return `${item.name || '未命名优惠券'} | ¥${item.price || 0} | 满${item.require || 0}可用`
    },
    handleStatusChange (checked) {
      this.form.status = checked ? 1 : 0
    },
    validateStartTime (rule, value, callback) {
      if (!value) {
        callback(new Error('请选择开始时间'))
        return
      }
      if (this.form.endTime && value >= this.form.endTime) {
        callback(new Error('开始时间必须早于结束时间'))
        return
      }
      callback()
    },
    validateEndTime (rule, value, callback) {
      if (!value) {
        callback(new Error('请选择结束时间'))
        return
      }
      if (this.form.startTime && value <= this.form.startTime) {
        callback(new Error('结束时间必须晚于开始时间'))
        return
      }
      callback()
    },
    buildSubmitPayload () {
      const payload = {
        name: this.form.name,
        couponId: this.form.couponId === undefined || this.form.couponId === null ? undefined : Number(this.form.couponId),
        activityType: this.form.activityType,
        startTime: this.form.startTime,
        endTime: this.form.endTime,
        status: this.form.status,
        remark: this.form.remark
      }
      if (this.form.id) {
        payload.id = this.form.id
      }
      return payload
    },
    submitForm () {
      if (this.submitLoading) {
        return
      }
      this.submitLoading = true
      this.$refs.form.validate(valid => {
        if (!valid) {
          this.submitLoading = false
          return false
        }
        const request = this.form.id ? updateCouponActivity : addCouponActivity
        request(this.buildSubmitPayload()).then(() => {
          this.$message.success(this.form.id ? '修改成功' : '新增成功', 3)
          this.open = false
          this.$emit('ok')
          this.reset()
        }).finally(() => {
          this.submitLoading = false
        })
      })
    }
  }
}
</script>
